/**
 * useUnifiedRecipientSearch
 *
 * Unified search hook across carriers, medical providers, and police departments.
 * Returns grouped results with entity type metadata.
 *
 * Uses the unified recipient-directory API endpoint for server-side search.
 */

import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { enhancedApi } from '@/services/api/generated/recipientDirectoryService';

import type { SearchableEntityType } from '../types';

export type { SearchableEntityType };

export interface UnifiedRecipientResult {
  id: string;
  name: string;
  type: SearchableEntityType;
  subtitle?: string;
}

export interface GroupedRecipientResult {
  type: SearchableEntityType;
  label: string;
  items: UnifiedRecipientResult[];
}

interface UseUnifiedRecipientSearchOptions {
  query: string;
  enabled?: boolean;
}

export const useUnifiedRecipientSearch = (
  options: UseUnifiedRecipientSearchOptions,
) => {
  const { query, enabled = true } = options;
  const debouncedQuery = useDebouncedValue(query, 200);
  const hasQuery = debouncedQuery.trim().length > 0;

  const { data, isLoading, isFetching } =
    enhancedApi.useRecipientDirectorySearchQuery(
      { q: debouncedQuery },
      { skip: !enabled || !hasQuery },
    );

  // API returns RecipientSearchResponse[] but we only need the first element
  // (The array wrapper is an OpenAPI codegen quirk for list endpoints)
  const response = Array.isArray(data) ? data[0] : data;

  // Map backend response to expected frontend format
  const results: UnifiedRecipientResult[] =
    response?.results?.map((r) => ({
      id: r.id,
      name: r.name,
      type: r.entity_type as SearchableEntityType,
      subtitle: r.subtitle ?? undefined,
    })) ?? [];

  const groupedResults: GroupedRecipientResult[] =
    response?.grouped_results?.map((g) => ({
      type: g.entity_type as SearchableEntityType,
      label: g.label,
      items: g.items.map((item) => ({
        id: item.id,
        name: item.name,
        type: item.entity_type as SearchableEntityType,
        subtitle: item.subtitle ?? undefined,
      })),
    })) ?? [];

  return {
    results,
    groupedResults,
    isLoading,
    isFetching,
    hasQuery,
    query: debouncedQuery,
  };
};
